import React, { useState, useEffect } from 'react';
import { Box, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../../amplify/data/resource';
import ReactLoading from 'react-loading';
import NewBadge from '../../../../components/icons/NewBadge';
import { generateClient } from 'aws-amplify/api';
import SalaryMonthsDisplay from '../../../../components/custom/SalaryMonthsDisplay';
import EmployeesList from '../../Employees/EmployeesList/EmployeesList';
import { useAuth } from '../../../../utils/context/useAuthContext';

// @ts-ignore
type File = Schema['File']['type'];
// @ts-ignore
type Employee = Schema['Employee']['type'];

interface FileItemProps {
  file: File;
}

const getFileIcon = (status: String) => {
  switch (status) {
    case 'COMPLETED':
      return '/assets/dashboard/fileGreen.svg';
    case 'IN_PROGRESS':
      return '/assets/dashboard/fileYellow.svg';
    case 'PAID':
      return '/assets/dashboard/fileYellow.svg';
    case 'PENDING':
      return '/assets/dashboard/fileGrey.svg';
    case 'REJECTED':
      return '/assets/dashboard/fileRed.svg';
    default:
      return '/assets/dashboard/fileGrey.svg';
  }
};

const getFileIconTextColor = (status: String) => {
  switch (status) {
    case 'COMPLETED':
      return '#94C729';
    case 'IN_PROGRESS':
      return '#F6B209';
    case 'PAID':
      return '#F6B209';
    case 'PENDING':
      return '#808080';
    case 'REJECTED':
      return '#FF6F78';
    default:
      return '#808080';
  }
};

const apiClient = generateClient<Schema>();

const FileItem: React.FC<FileItemProps> = ({ file }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openDetails, setOpenDetails] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const handleToggleDetails = () => {
    setOpenDetails((prev) => !prev);
  };

  // Загружаем employees для этого файла через API
  useEffect(() => {
    if (openDetails && !loadingEmployees && user?.userId) {
      const fetchEmployees = async () => {
        setLoadingEmployees(true);
        try {
          // Загружаем employees связанных с этим файлом
          const { data: fetchedEmployeesData, errors } =
            await apiClient.models.Employee.listEmployeeByFileId({
              fileId: file.id,
            }, {
              filter: {
                and: [
                  {
                    or: [
                      { status: { eq: 'IN_PROGRESS' } },
                      { status: { eq: 'RECORD_CAPTURED_SUCCESS' } }
                    ]
                  },
                ]
              }
            });

          if (errors && errors.length > 0) {
            console.error('Errors fetching employees:', errors);
          }

          const currentEmployees = fetchedEmployeesData || [];
          setEmployees([...currentEmployees]);
        } catch (error) {
          console.error('Exception during fetching employees:', error);
        } finally {
          setLoadingEmployees(false);
        }
      };

      fetchEmployees();
    }
  }, [openDetails, user?.userId, file.id]);

  const totalToBePaid = Array.isArray(file?.deposits)
    ? file.deposits.reduce(
      (accumulator: number, deposit: { totalDepositAmount?: number }) => {
        return accumulator + (deposit.totalDepositAmount || 0);
      },
      0,
    )
    : 0;

  const fileEmployees: Employee[] = Array.isArray(file?.deposits)
    ? file.deposits
      .flatMap((deposit: any) => deposit.employees || [])
      .reduce((uniqueEmployees: Employee[], currentEmployee: Employee) => {
        if (
          !uniqueEmployees.some(
            (e) => e.employeeId === currentEmployee.employeeId,
          )
        ) {
          uniqueEmployees.push(currentEmployee);
        }
        return uniqueEmployees;
      }, [])
    : [];

  const numberOfDeposits = file?.deposits?.length?.toString().padStart(2, '0');

  const fileNavigate = async (fileId: string) => {
    const updateReportResponse = await apiClient.models.File.update({
      id: fileId,
      viewed: true,
    });
    navigate(`/file-overview?fileId=${fileId}`);
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'טרם נשלח';
      case 'IN_PROGRESS':
        return 'בתהליך';
      case 'PAID':
        return 'הושלם';
      case 'COMPLETED':
        return 'הושלם';
    }
  };

  const getMislakaStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'טרם נשלח';
      case 'IN_PROGRESS':
        return 'טרם נשלח';
      case 'PAID':
        return 'בתהליך';
      case 'COMPLETED':
        return 'נקלט בהצלחה';
    }
  };

  return (
    <>
      {file.status === 'PROCESSING' ? (
        <TableRow
          sx={{
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        >
          <TableCell
            colSpan={9}
            sx={{ textAlign: 'center', borderRadius: '10px' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ py: 0 }}
            >
              <ReactLoading
                type={'bubbles'}
                color={'#94C729'}
                height={30}
                width={50}
              />
              <Typography
                fontWeight="500"
                fontSize="16px"
                fontFamily="Heebo"
                color="#94C729"
              >
                עיבוד קובץ
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      ) : file.status === 'ERROR' ? (
        <TableRow
          sx={{
            backgroundColor: '#FDF2F2',
            borderRadius: '10px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        >
          <TableCell
            colSpan={9}
            sx={{ textAlign: 'center', borderRadius: '10px' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={0.5}
              sx={{ py: 0 }}
            >
              <Icon
                icon="mdi:close-circle-outline"
                color="#FF6F78"
                width={40}
                height={40}
              />
              <Typography
                fontWeight="500"
                fontSize="16px"
                fontFamily="Heebo"
                color="#FF6F78"
              >
                הקובץ של {format(new Date(file.date), 'MMMM', { locale: he })}{' '}
                {format(new Date(file.date), 'yyyy', { locale: he })} אינו תואם
                את הפורמט
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      ) : (
        <TableRow
          sx={{
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            p: '20px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
            cursor: 'pointer',
            position: 'relative',
          }}
          onClick={() => fileNavigate(file.id)}
        >
          <TableCell
            sx={{
              py: '20px',
              px: '30px',
              borderBottom: 'none',
              borderTopRightRadius: '10px',
              borderBottomRightRadius: '10px',
            }}
          >
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '2px',
              }}
            >
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
              >
                {format(new Date(file.createdAt), 'dd/MM/yyyy', { locale: he })}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              py: '20px',
              px: '10px',
              borderBottom: 'none',
              borderTopRightRadius: '10px',
              borderBottomRightRadius: '10px',
            }}
          >
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '2px',
              }}
            >
              <SalaryMonthsDisplay employees={fileEmployees} />
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <img
                alt="file icon"
                src={getFileIcon(file.status || 'PENDING')}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                }}
              >
                <Typography
                  fontWeight={'500'}
                  fontSize={'20px'}
                  fontFamily={'Montserrat'}
                  color={getFileIconTextColor(file.status || 'PENDING')}
                >
                  {numberOfDeposits}
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'14px'}
                  color={getFileIconTextColor(file.status || 'PENDING')}
                  fontFamily={'AlmoniDL'}
                >
                  הוראות תשלום
                </Typography>
              </Box>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {file?.senderName}
            </Typography>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <img alt="card" src={'/assets/dashboard/card.svg'} />
              <Typography
                fontWeight={'500'}
                fontSize={'20px'}
                fontFamily={'Montserrat'}
              >
                {fileEmployees?.length?.toString()?.padStart(2, '0')}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <img src={'/assets/dashboard/coins.svg'} alt="coins" />
              <Typography
                fontWeight={'500'}
                fontSize={'20px'}
                fontFamily={'Montserrat'}
              >
                {totalToBePaid.toLocaleString()}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
              {file.status === 'COMPLETED' && (
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#94C729',
                  }}
                />
              )}
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
              >
                {getPaymentStatusText(file.status || 'PENDING')}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
              {file.status === 'COMPLETED' && (
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#94C729',
                  }}
                />
              )}
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
              >
                {getMislakaStatusText(file.status || 'PENDING')}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              borderTopLeftRadius: '10px',
              borderBottomLeftRadius: '10px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Tooltip title='פירוט' slotProps={{
                tooltip: {
                  sx: {
                    fontFamily: 'AlmoniDL',
                    fontSize: '12px',
                    fontWeight: '700',
                  },
                },
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '45px',
                    height: '45px',
                    background: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleDetails();
                  }}
                >
                  <Icon
                    icon={openDetails ? "mdi:chevron-up" : "mdi:chevron-down"}
                    width={20}
                    height={20}
                    color="#666"
                  />
                </Box>
              </Tooltip>
            </Box>
          </TableCell>
          {!file.viewed && (
            <Box sx={{ position: 'absolute', top: 0, right: 5, zIndex: 1 }}>
              <NewBadge />
            </Box>
          )}
        </TableRow>
      )}

      {/* Expandable row for EmployeesList */}
      {openDetails && file.status !== 'PROCESSING' && file.status !== 'ERROR' && (
        <TableRow>
          <TableCell
            colSpan={9}
            sx={{
              borderBottom: 'none',
              backgroundColor: 'transparent',
              padding: 0
            }}
          >
            <Box sx={{
              backgroundColor: 'transparent',
              margin: '0 20px 20px 20px',
              borderRadius: '10px',
              padding: '20px'
            }}>
              {loadingEmployees ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <ReactLoading
                    type={'bubbles'}
                    color={'#94C729'}
                    height={50}
                    width={100}
                  />
                </Box>
              ) : employees.length > 0 ? (
                <EmployeesList employees={employees} />
              ) : (
                <Typography
                  textAlign="center"
                  fontFamily={'AlmoniDL'}
                  color="#666"
                  py={4}
                >
                  אין שיוכים להצגה עבור קובץ זה
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default FileItem;
